import { useState } from 'react'

import { Button } from '@/ui/button'
import { FilterSelect } from '@/ui/filter-select'
import { Input } from '@/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/select'

import { useFilterSource } from './data-grid-filter-source'
import {
  DataGridFilterBody,
  DataGridFilterHeader,
  DataGridFilterPanel,
} from './data-grid-filter-panel'
import {
  DataGridFilterChip,
  dataGridFilterSummary,
  filterInputValue,
  keepKeysOutOfMenu,
  operatorLabel,
  type DataGridFilterPresentationProps,
} from './data-grid-filter-shared'

export function DataGridValueFilter(props: DataGridFilterPresentationProps) {
  return <DataGridScalarFilter {...props} />
}

function DataGridScalarFilter({
  column,
  presentation,
  onBack,
  onApplied,
}: DataGridFilterPresentationProps) {
  const grid = useFilterSource()
  const current = grid.filters?.find((filter) => filter.column === column.id)
  const currentOperator = current?.operator ?? column.operators[0] ?? 'equals'
  const currentFirst = filterInputValue(current?.values, 'first')
  const currentSecond = filterInputValue(current?.values, 'second')
  const [open, setOpen] = useState(false)
  const [operator, setOperator] = useState(currentOperator)
  const [first, setFirst] = useState(currentFirst)
  const [second, setSecond] = useState(currentSecond)
  const numberFirst = column.kind === 'number' ? parseNumber(first) : null
  const numberSecond = column.kind === 'number' ? parseNumber(second) : null
  const canApply = operator === 'empty' || (column.kind === 'number'
    ? numberFirst !== null && (operator !== 'between'
      || numberSecond !== null && numberFirst <= numberSecond)
    : first.trim().length > 0 && (operator !== 'between' || second.trim().length > 0))

  function resetDraft() {
    setOperator(currentOperator)
    setFirst(currentFirst)
    setSecond(currentSecond)
  }

  function changeOpen(next: boolean) {
    if (next) resetDraft()
    setOpen(next)
  }

  function apply() {
    if (!canApply) return
    const firstValue = column.kind === 'number' ? numberFirst! : first
    const values = operator === 'empty'
      ? true
      : operator === 'between'
        ? column.kind === 'number'
          ? { from: numberFirst!, to: numberSecond! }
          : { from: first, to: second }
        : operator === 'relative'
          ? { amount: Math.max(1, Number(first) || 1), unit: second === 'hours' ? 'hours' as const : 'days' as const }
          : firstValue
    grid.setFilters([
      ...(grid.filters ?? []).filter((filter) => filter.column !== column.id),
      { column: column.id, operator, values },
    ])
    setOpen(false)
    onApplied?.()
  }

  function clear() {
    grid.setFilters((grid.filters ?? []).filter((filter) => filter.column !== column.id))
    setOpen(false)
    onApplied?.()
  }

  const content = (
    <DataGridFilterPanel presentation={presentation} className="w-72">
      <DataGridFilterHeader label={column.label} onBack={onBack} />
      <DataGridFilterBody>
        <Select
          value={operator}
          items={Object.fromEntries(column.operators.map((item) => [item, operatorLabel(item)]))}
          onValueChange={(next) => next && setOperator(next)}
        >
          <SelectTrigger aria-label={`${column.label} operator`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {column.operators.map((item) => (
              <SelectItem key={item} value={item}>{operatorLabel(item)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {operator !== 'empty' ? (
          <Input
            aria-label={`${column.label} value`}
            type={column.kind === 'number' ? 'number' : 'text'}
            step={column.kind === 'number' ? 1 : undefined}
            value={first}
            onChange={(event) => setFirst(event.target.value)}
            onKeyDown={keepKeysOutOfMenu}
            placeholder={operator === 'relative' ? 'Amount' : 'Value'}
          />
        ) : null}
        {operator === 'between' ? (
          <Input
            aria-label={`${column.label} end value`}
            type={column.kind === 'number' ? 'number' : 'text'}
            step={column.kind === 'number' ? 1 : undefined}
            value={second}
            onChange={(event) => setSecond(event.target.value)}
            onKeyDown={keepKeysOutOfMenu}
          />
        ) : null}
        {operator === 'relative' ? (
          <FilterSelect
            hideLabel
            label={`${column.label} relative unit`}
            className="w-full"
            value={second || 'days'}
            onChange={setSecond}
            items={{ hours: 'Hours', days: 'Days' }}
          />
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" size="sm" variant="ghost" disabled={!current} onClick={clear}>
            Clear
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canApply}
            onClick={apply}
          >
            Apply
          </Button>
        </div>
      </DataGridFilterBody>
    </DataGridFilterPanel>
  )

  if (presentation !== 'chip') return content
  return (
    <DataGridFilterChip
      column={column}
      summary={dataGridFilterSummary(current, column)}
      open={open}
      onOpenChange={changeOpen}
    >
      {content}
    </DataGridFilterChip>
  )
}

function parseNumber(value: string): number | null {
  if (!/^-?\d+$/.test(value.trim())) return null
  const number = Number(value)
  return Number.isInteger(number) && number >= -2147483648 && number <= 2147483647
    ? number
    : null
}

export function DataGridTextFilter({
  column,
  presentation,
  onBack,
  onApplied,
}: DataGridFilterPresentationProps) {
  const grid = useFilterSource()
  const current = grid.filters?.find((filter) => filter.column === column.id)
  const currentOperator = current?.operator ?? column.operators[0] ?? 'contains'
  const currentValue = typeof current?.values === 'string' ? current.values : ''
  const [open, setOpen] = useState(false)
  const [operator, setOperator] = useState(currentOperator)
  const [value, setValue] = useState(currentValue)

  function resetDraft() {
    setOperator(currentOperator)
    setValue(currentValue)
  }

  function changeOpen(next: boolean) {
    if (next) resetDraft()
    setOpen(next)
  }

  function replaceFilter(next?: { operator: string; values: string | boolean }) {
    grid.setFilters([
      ...(grid.filters ?? []).filter((filter) => filter.column !== column.id),
      ...(next ? [{ column: column.id, ...next }] : []),
    ])
    setOpen(false)
    onApplied?.()
  }

  const content = (
    <DataGridFilterPanel presentation={presentation} className="w-72">
      <DataGridFilterHeader label={column.label} onBack={onBack} />
      <DataGridFilterBody>
        <Select
          value={operator}
          items={Object.fromEntries(column.operators.map((item) => [item, operatorLabel(item)]))}
          onValueChange={(next) => next && setOperator(next)}
        >
          <SelectTrigger aria-label={`${column.label} operator`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {column.operators.map((item) => (
              <SelectItem key={item} value={item}>{operatorLabel(item)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {operator !== 'empty' ? (
          <Input
            aria-label={`${column.label} value`}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={keepKeysOutOfMenu}
            placeholder="Value"
          />
        ) : null}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={!current}
            onClick={() => replaceFilter()}
          >
            Clear
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={operator !== 'empty' && !value.trim()}
            onClick={() => replaceFilter({
              operator,
              values: operator === 'empty' ? true : value,
            })}
          >
            Apply
          </Button>
        </div>
      </DataGridFilterBody>
    </DataGridFilterPanel>
  )

  if (presentation !== 'chip') return content
  return (
    <DataGridFilterChip
      column={column}
      summary={dataGridFilterSummary(current, column)}
      open={open}
      onOpenChange={changeOpen}
    >
      {content}
    </DataGridFilterChip>
  )
}
